import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_BASE } from '../lib/config';
import { useUserStore } from './userStore';

// =============================================================================
// Types
// =============================================================================

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
}

interface SetAuthParams {
  token: string;
  user: AuthUser;
  hasZoomConnection?: boolean;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  hasZoomConnection: boolean;

  // Actions
  setAuth: (params: SetAuthParams) => void;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  setZoomConnected: (connected: boolean) => void;
}

// =============================================================================
// Store
// =============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      hasZoomConnection: false,

      setAuth: ({ token, user, hasZoomConnection }) => {
        set({
          token,
          user,
          ...(hasZoomConnection !== undefined ? { hasZoomConnection } : {}),
        });
      },

      logout: async () => {
        const { token } = get();
        if (token) {
          try {
            await fetch(`${API_BASE}/auth/logout`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tenant-ID': '',
              },
            });
          } catch {
            // best-effort — clear state regardless
          }
        }
        set({
          token: null,
          user: null,
          hasZoomConnection: false,
        });
        // Clear userStore to prevent stale tenant/user data
        useUserStore.getState().logout();
        window.location.href = '/';
      },

      checkSession: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await fetch(`${API_BASE}/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Tenant-ID': '',
            },
          });

          if (!response.ok) {
            // Session invalid — clear token, let ProtectedRoute redirect
            set({ token: null, user: null, hasZoomConnection: false });
            return;
          }

          const data = await response.json();
          set({
            user: data.user,
            hasZoomConnection: data.hasZoomConnection,
          });
        } catch {
          // Network error — don't clear session on transient errors
          // The token may still be valid
        }
      },

      setZoomConnected: (connected: boolean) => {
        set({ hasZoomConnection: connected });
      },
    }),
    {
      name: 'zac-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        hasZoomConnection: state.hasZoomConnection,
      }),
    }
  )
);

// =============================================================================
// Helper: get current token for API client
// =============================================================================

export function getToken(): string | null {
  return useAuthStore.getState().token;
}
